import * as dateFns from 'date-fns'
import React from 'react'

import {
  Quote,
  useChartLayoutGlobalModelAsync,
  withCompleteLayoutModelInit,
  withQuotePolling,
} from '../../../../../app/charts-lib'
import { formatDate, getDate } from '../../../../../app/header/utils'
import { PortalContext } from '../../../../components/Portal'
import { QuotePrice } from '../QuotePrice'
import { HeaderPricesContext, useChartContextPrices } from './hooks'

export function ChartContextQuoteHeader() {
  const { toggleStaticElement } = React.useContext(PortalContext)
  const { chartLayoutModel } = useChartLayoutGlobalModelAsync()
  const data = useChartContextPrices(chartLayoutModel)
  const hasData = !!data

  React.useEffect(() => {
    if (hasData) toggleStaticElement(true)

    return () => toggleStaticElement(false)
  }, [hasData, toggleStaticElement])

  return <QuoteHeaderContent data={data} />
}

interface QuoteHeaderWithManualInitProps {
  chartLayoutModel: any
  config: any
}

function ManualInitQuoteHeaderComponent({ chartLayoutModel, config }: QuoteHeaderWithManualInitProps) {
  const { toggleStaticElement } = React.useContext(PortalContext)
  const data = useChartContextPrices(chartLayoutModel)

  /**
   * We need to instantiate layout model in order to use quote refetch on page
   * without an actual chart (options.) We also need to manually create a chart model
   *
   * It is then correctly caught in `useChartContextPrices`
   */
  React.useEffect(() => {
    async function fetchAndMapInitialQuote() {
      for (const chartConfig of config.charts) {
        const modelProperties = Object.assign({}, chartConfig)

        modelProperties.chart_layout = chartLayoutModel
        delete modelProperties.panes
        if (modelProperties.refreshData == null) {
          modelProperties.refreshData = true
        }
        chartLayoutModel.charts().create(modelProperties)
      }

      const chart = chartLayoutModel.charts().first()

      const quote = await Quote.get({
        ticker: chart.ticker,
        instrument: chart.instrument,
        timeframe: chart.timeframe,
        options: { barsCount: 0 },
      })

      chart.updateAttributes({ quote })
      toggleStaticElement()
    }

    void fetchAndMapInitialQuote()
  }, [chartLayoutModel, config.charts, toggleStaticElement])

  return <QuoteHeaderContent data={data} />
}

export const ManualInitQuoteHeader = withCompleteLayoutModelInit(withQuotePolling(ManualInitQuoteHeaderComponent))

export function QuoteHeaderContent({ data }: { data: HeaderPricesContext | null }) {
  if (!data) return null

  const isChange = Number.isFinite(data.prevClose)
  const isAfterChange = Number.isFinite(data.afterClose) && data.afterClose !== data.lastClose
  const lastTime = data.lastTime ? getDate(data.lastTime * 1000) : null
  const lastDate = !lastTime && data.lastDate ? formatDate(data.lastDate, 'yyyyMMdd', 'MMM dd, yyyy') : null
  const afterDate = data.afterTime ? getDate(data.afterTime * 1000) : null

  let afterhoursTitle
  if (lastTime && afterDate) {
    if (dateFns.isBefore(afterDate, lastTime)) afterhoursTitle = 'Premarket'
    else afterhoursTitle = 'Aftermarket'
  }

  return (
    <>
      {isChange && (
        <QuotePrice
          title="Last Close"
          price={data.lastClose}
          date={lastTime}
          dateLabel={lastDate}
          changeUsd={data.lastClose - data.prevClose}
          changePct={(data.lastClose / data.prevClose - 1) * 100}
        />
      )}
      {isAfterChange && (
        <QuotePrice
          title={`${afterhoursTitle} Close`}
          price={data.afterClose}
          date={afterDate}
          dateLabel={afterhoursTitle}
          changeUsd={data.afterClose - data.lastClose}
          changePct={(data.afterClose / data.lastClose - 1) * 100}
        />
      )}
    </>
  )
}
